<template>
    <fieldset>
        <div class="input-group mb-3">
            <div class="input-group-prepend">
                <div class="input-group-text">Miljö</div>
            </div>
            <select
                class="custom-select"
                required
                v-model="environment"
                @change="onUpdate"
            >
                <option value="production">Produktionsmiljö</option>
                <option value="playground">Testmiljö</option>
            </select>
        </div>

        <div class="input-group mb-3">
            <div class="input-group-prepend">
                <div class="input-group-text">Autentiseringsmetod</div>
            </div>
            <select
                class="custom-select"
                required
                v-model="authenticationMethod"
                @change="onUpdate"
            >
                <option value="username-password">
                    Användarnamn och lösenord
                </option>
                <option value="api-key">API-nyckel</option>
            </select>
        </div>
        <template v-if="authenticationMethod === 'api-key'">
            <div class="input-group mb-3">
                <div class="input-group-prepend">
                    <div class="input-group-text">API-nyckel</div>
                </div>
                <input
                    type="password"
                    class="form-control"
                    required
                    v-model="apiKey"
                    @input="onUpdate"
                />
            </div>
        </template>
        <template v-else>
            <div class="input-group mb-3">
                <div class="input-group-prepend">
                    <div class="input-group-text">Username</div>
                </div>
                <input
                    type="text"
                    class="form-control"
                    required
                    v-model="username"
                    @input="onUpdate"
                />
            </div>
            <div class="input-group mb-3">
                <div class="input-group-prepend">
                    <div class="input-group-text">Password</div>
                </div>
                <input
                    type="password"
                    class="form-control"
                    required
                    v-model="password"
                    @input="onUpdate"
                />
            </div>
        </template>
        <div class="input-group mb-3">
            <div class="input-group-prepend">
                <div class="input-group-text">Länk till era köpvilkor</div>
            </div>
            <input
                type="url"
                class="form-control"
                required
                v-model="termsUrl"
                @input="onUpdate"
            />
        </div>

        <div class="input-group mb-3">
            <div class="input-group-prepend">
                <div class="input-group-text">Fältnamn: Adressrad 1</div>
            </div>
            <input
                type="text"
                class="form-control"
                required
                v-model="fieldAddress1"
                @input="onUpdate"
            />
        </div>

        <div class="input-group mb-3">
            <div class="input-group-prepend">
                <div class="input-group-text">Fältnamn: Adressrad 2</div>
            </div>
            <input
                type="text"
                class="form-control"
                v-model="fieldAddress2"
                @input="onUpdate"
            />
        </div>

        <div class="input-group mb-3">
            <div class="input-group-prepend">
                <div class="input-group-text">Fältnamn: Postnr</div>
            </div>
            <input
                type="text"
                class="form-control"
                required
                v-model="fieldZipCode"
                @input="onUpdate"
            />
        </div>

        <div class="input-group mb-3">
            <div class="input-group-prepend">
                <div class="input-group-text">Fältnamn: City</div>
            </div>
            <input
                type="text"
                class="form-control"
                required
                v-model="fieldCity"
                @input="onUpdate"
            />
        </div>

        <div class="input-group mb-3">
            <div class="input-group-prepend">
                <div class="input-group-text">Fältnamn: ID. nr</div>
            </div>
            <input
                type="text"
                class="form-control"
                v-model="fieldIdNumber"
                @input="onUpdate"
            />
        </div>

        <div class="input-group mb-3">
            <div class="input-group-prepend">
                <div class="input-group-text">Fältnamn: CheckoutOrderId</div>
            </div>
            <input
                type="text"
                class="form-control"
                v-model="fieldCheckoutOrderId"
                @input="onUpdate"
            />
        </div>
    </fieldset>
</template>

<script setup lang="ts">
import { computed, ref, toRefs, watch } from "vue";
const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  }
});
const {
  modelValue
} = toRefs(props);
const emit = defineEmits(["update:modelValue"]);
const authenticationMethod = ref("username-password");
const username = ref(null);
const password = ref(null);
const apiKey = ref(null);
const termsUrl = ref(null);
const environment = ref(null);
const fieldAddress1 = ref(null);
const fieldAddress2 = ref(null);
const fieldZipCode = ref(null);
const fieldCity = ref(null);
const fieldIdNumber = ref(null);
const fieldCheckoutOrderId = ref(null);
const serializedValue = computed(() => {
  return {
    authentication_method: authenticationMethod.value,
    username: username.value,
    password: password.value,
    api_key: apiKey.value,
    terms_url: termsUrl.value,
    environment: environment.value,
    field_address1: fieldAddress1.value,
    field_address2: fieldAddress2.value,
    field_zip_code: fieldZipCode.value,
    field_city: fieldCity.value,
    field_id_number: fieldIdNumber.value,
    field_checkout_order_id: fieldCheckoutOrderId.value
  };
});
function onUpdate() {
    emit("update:modelValue", serializedValue.value);
}
watch(modelValue, () => {
  if (JSON.stringify(modelValue.value) !== JSON.stringify(serializedValue.value)) {
    authenticationMethod.value = modelValue.value.authentication_method ?? "username-password";
    username.value = modelValue.value.username;
    password.value = modelValue.value.password;
    apiKey.value = modelValue.value.api_key;
    termsUrl.value = modelValue.value.terms_url;
    environment.value = modelValue.value.environment;
    fieldAddress1.value = modelValue.value.field_address1;
    fieldAddress2.value = modelValue.value.field_address2;
    fieldZipCode.value = modelValue.value.field_zip_code;
    fieldCity.value = modelValue.value.field_city;
    fieldIdNumber.value = modelValue.value.field_id_number;
    fieldCheckoutOrderId.value = modelValue.value.field_checkout_order_id;
    if (!fieldAddress1.value) {
      fieldAddress1.value = "Adress";
    }
    if (!fieldZipCode.value) {
      fieldZipCode.value = "Postnummer";
    }
    if (!fieldCity.value) {
      fieldCity.value = "Ort";
    }
    if (JSON.stringify(modelValue.value) !== JSON.stringify(serializedValue.value)) {
      onUpdate();
    }
  }
}, {
  immediate: true
});
</script>
